import React, { useState } from 'react';
import { Form, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { PhoneOutlined, MailOutlined, ShopOutlined } from '@ant-design/icons';
import { H1 } from '../../../../components/ui/Typography';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import SessionForm from '../../../../components/forms/SessionForm';
import ScreenNavigationModel, {
  ScreenType
} from '../../models/ScreenNavigationModel';
import BusinessModel from '../../models/BusinessModel';
import PhoneValidator from '../../../../helpers/antd-forms/PhoneValidator';
import ZipValidator from '../../../../helpers/antd-forms/ZipValidator';
import StreetNumberValidator from '../../../../helpers/antd-forms/StreetNumberValidator';
import HelpBoxCompanionModel from '../../../../models/HelpBoxCompanionModel';
import { motion, AnimatePresence } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../../components/animations/StepAnimations';
import { observer } from 'mobx-react-lite';

interface CompanyDataProps { }

const EnterYourCompanyData: React.FC<CompanyDataProps> = observer(() => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [isValid, setIsValid] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [nextScreen, setNextScreen] = useState<ScreenType | null>(null);

  const handleFieldsChange = () => {
    const allTouched = form.isFieldsTouched(true);
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsValid(allTouched && !hasErrors);
  };

  const handleScreenChange = () => {
    setIsVisible(false);
  };

  const handleSave = React.useCallback(async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      //construct the combined "address" field
      let { street, houseNumber, city, state } = values;
      let streetWithNumber = street + ' ' + houseNumber;

      const addressParts = [streetWithNumber, city, state].filter(Boolean);
      BusinessModel.manualCompanyData = {
        ...values,
        address: addressParts.join(', '),
        street: streetWithNumber,
      };

      // Use fade out animation before navigation
      setNextScreen(ScreenType.CHOOSE_CORPORATE_CATEGORY);
      handleScreenChange();
    } catch (error) {
      message.error(t('Please fill in all required fields'));
      return;
    }
  }, [form, t]);

  const helpBoxTitleMessage = t('Why do we need your contact information?');
  const helpBoxParagraphMessage = t("We use your contact details to display them on your new website – so your visitors know how to reach you.Make sure everything is correct – this info helps build trust and makes it easier for potential customers to get in touch.");

  React.useEffect(() => {
    HelpBoxCompanionModel.setDisplayHelpBox(true);
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (nextScreen) {
          setTimeout(() => {
            ScreenNavigationModel.setCurrentScreen(nextScreen);
          }, 200);
        }
      }}
    >
      {isVisible && (
        <motion.div
          className="flex items-start sm:items-center sm:text-center justify-start sm:justify-center flex-1"
          initial="hidden"
          animate="visible"
          variants={staggerContainerVariants}
          exit="hidden"
        >
          <div className="company-data-form">
            <motion.div variants={fadeInUpVariants}>
              <H1>{t('Enter your company data')}</H1>
            </motion.div>

            <SessionForm
              form={form}
              formName="enterYourCompanyDataForm"
              formProps={{
                layout: 'vertical',
                onFieldsChange: handleFieldsChange,
                onFinish: handleSave
              }}>
              <motion.div variants={fadeInUpVariants}>
                <Form.Item
                  label={t('Your company name')}
                  name="company_name"
                  rules={[{ required: true, message: t('Required') }]}>
                  <Input
                    placeholder={t('e.g. Max Mustermann GmbH')}
                    size="large"
                    prefix={<ShopOutlined />}
                  />
                </Form.Item>
              </motion.div>

              <motion.div variants={fadeInUpVariants}>
                <div className="flex flex-col md:flex-row gap-[24px] md:gap-[18px] mb-[24px]">
                  <div className="flex-1">
                    <Form.Item
                      label={t('Street and house number')}
                      required
                      style={{ marginBottom: 0 }}>
                      <div className="flex gap-2">
                        <Form.Item
                          name="street"
                          noStyle
                          rules={[{ required: true, message: t('Required') }]}>
                          <Input
                            className="flex-[3]"
                            placeholder={t('Street')}
                            size="large"
                          />
                        </Form.Item>
                        <Form.Item
                          name="houseNumber"
                          noStyle
                          rules={[
                            { required: true, message: 'Required' },
                            StreetNumberValidator(
                              t('Invalid street number. Please use numbers first, then optional letters (A-Z), dashes ( - ), slashes ( / ), or fractions (½). No special symbols like @ or #.')
                            )
                          ]}>
                          <Input
                            className="flex-[2]"
                            placeholder={t('House number')}
                            size="large"
                          />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </div>

                  <div className="hidden md:flex items-center justify-center relative top-[15px]">
                    /
                  </div>

                  <div className="flex-1">
                    <Form.Item
                      label={t('Postal code and city')}
                      required
                      style={{ marginBottom: 0 }}>
                      <div className="flex gap-2">
                        <Form.Item
                          name="zip"
                          noStyle
                          rules={[
                            { required: true, message: 'Required' },
                            ZipValidator(
                              t('Invalid ZIP code. Please use only numbers, letters, spaces, or a hyphen ( - ).')
                            )
                          ]}>
                          <Input
                            className="flex-[2]"
                            placeholder={t('Postal code')}
                            size="large"
                          />
                        </Form.Item>
                        <Form.Item
                          name="city"
                          noStyle
                          rules={[{ required: true, message: t('Required') }]}>
                          <Input
                            className="flex-[3]"
                            placeholder={t('City')}
                            size="large"
                          />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUpVariants}>
                <div className="flex flex-col md:flex-row gap-[18px]">
                  <div className="flex-1">
                    <Form.Item
                      label={t('Telephone number')}
                      name="phone"
                      rules={[
                        { required: true, message: t('Required') },
                        PhoneValidator(t('Phone number must begin with international country code (e.g. +1) and may only contain numbers (format E164).'))
                      ]}>
                      <Input
                        placeholder={t('Your Phone Number')}
                        size="large"
                        prefix={<PhoneOutlined />}
                      />
                    </Form.Item>
                  </div>
                  <div className="flex-1">
                    <Form.Item
                      label={t('E-mail address')}
                      name="email"
                      rules={[
                        { required: true, message: t('Required') },
                        { type: 'email', message: t('Please enter a valid e-mail address') }
                      ]}>
                      <Input
                        placeholder="johndoe@maildomain.com"
                        size="large"
                        prefix={<MailOutlined />}
                      />
                    </Form.Item>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUpVariants}>
                <div className="flex justify-center mt-8">
                  <AntdCustomButton
                    id="manual-company-info-save"
                    data-ga-event="manual-company-info-save"
                    appearance="important"
                    htmlType="submit"
                    size="large"
                    disabled={!isValid}
                    style={{ minWidth: 160, borderRadius: 999 }}>
                    {t('save')}
                  </AntdCustomButton>
                </div>
              </motion.div>
            </SessionForm>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default EnterYourCompanyData;
